import React, { useMemo } from "react"
import {
  Table,
  Tag,
  Space,
  Typography,
  message,
  Button,
  Dropdown,
  MenuProps,
} from "@mimber/ui-web"
import {
  DollarOutlined,
  ClockCircleOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@mimber/ui-web"
import { useMembershipTiers } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { MembershipTierDto, RecurrenceInterval } from "@mimber/schema"
import type { MembershipTiersTableProps } from "./MembershipTiersTable.types"
import { formatCurrency } from "@/utils"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

const RECURRENCE_LABELS: Record<RecurrenceInterval, string> = {
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  YEARLY: "Yearly",
}

const RECURRENCE_COLORS: Record<RecurrenceInterval, string> = {
  DAILY: "cyan",
  WEEKLY: "blue",
  MONTHLY: "purple",
  QUARTERLY: "magenta",
  YEARLY: "green",
}

export const MembershipTiersTable: React.FunctionComponent<
  MembershipTiersTableProps
> = ({ onRowClick, onEdit, onDelete, className, ...tableProps }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "membership-tiers",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
    })

  const { data: response, isLoading, error } = useMembershipTiers(queryParams)

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load membership tiers: ${error.message}`)
    }
  }, [error])

  const getActionMenuItems = (
    record: MembershipTierDto
  ): MenuProps["items"] => [
    {
      key: "edit",
      label: "Edit Tier",
      icon: <EditOutlined />,
      onClick: () => onEdit?.(record),
    },
    {
      key: "delete",
      label: "Delete Tier",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => onDelete?.(record),
    },
  ]

  const columns: ColumnsType<MembershipTierDto> = useMemo(
    () => [
      {
        title: "Tier",
        key: "tier",
        render: (_, record) => <Text strong>{record.name}</Text>,
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search tier name"),
      },
      {
        title: "Price",
        key: "price",
        render: (_, record) => (
          <Space>
            <DollarOutlined />
            <div>
              <Text strong>
                {record.price != null ? formatCurrency(record.price) : "Free"}
              </Text>
              {record.price && record.intervalType && (
                <div>
                  <Tag color={RECURRENCE_COLORS[record.intervalType]}>
                    {RECURRENCE_LABELS[record.intervalType]}
                  </Tag>
                </div>
              )}
            </div>
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Active",
        key: "isActive",
        render: (_, record) => (
          <Tag color={record.isActive ? "green" : "orange"}>
            {record.isActive ? "Active" : "Inactive"}
          </Tag>
        ),
        filters: [
          { text: "Active", value: "true" },
          { text: "Inactive", value: "false" },
        ],
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (createdAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text>{formatTableDate(createdAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 80,
        render: (_, record) => (
          <Dropdown
            menu={{
              items: getActionMenuItems(record),
            }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        ),
      },
    ],
    [onEdit, onDelete]
  )

  const handleRowClick = (record: MembershipTierDto) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const onTableChange: TableProps<MembershipTierDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    // Convert antd types to our utility function types
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<MembershipTierDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
